import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Loader2, EyeOff } from "lucide-react";
import { ENGINE_LABELS } from "@/config";
import { useGraphite, GraphiteScope } from "@/components/Graphite";
import { EngineWithLogo } from "@/components/ProviderLogo";
import { TextShimmer } from "@/components/Motion";
import AssistantButton, { useAssistantShown } from "@/components/AssistantButton";

// The handoff's page header: an uppercase eyebrow, then a two-line headline: what the page is (muted) and a status
// sentence worked out from live data (primary), with the page's main action at the bottom right. `subtitle` is the
// older one-line form, for the public and account pages.
export function PageHeader({ eyebrow, title, status, subtitle, actions: own, testid, live }) {
  const g = useGraphite();
  // a founder's every page offers their assistant first (owner, 2026-10-22)
  const assistant = useAssistantShown();
  const actions = assistant || own ? <>{assistant && <AssistantButton />}{own}</> : null;
  if (g && (eyebrow || status)) return (
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3" data-testid={testid}>
      <div className="flex flex-col gap-1.5 min-w-0">
        {eyebrow && (
          <span className="flex items-center gap-2 text-xs uppercase tracking-[0.09em] text-muted-foreground">
            {live && <span className="w-2 h-2 shrink-0 rounded-full bg-gr-live shadow-[0_0_0_3px_color-mix(in_srgb,var(--gr-live)_15%,transparent)]" aria-hidden="true" />}{eyebrow}
          </span>
        )}
        <h1 className="m-0 font-display text-[28px] leading-[1.2] tracking-[0.02em] font-semibold [text-wrap:balance]">
          <span className="block font-normal text-muted-foreground">{title}</span>
          {status && <span className="block">{status}</span>}
        </h1>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
  return (
    // in Graphite the actions move under the title when they don't fit beside it
    <div className={cn("flex items-start justify-between gap-4", g ? "flex-wrap mb-5" : "mb-6")} data-testid={testid}>
      <div>
        <h1 className={g ? "font-display font-semibold text-[20px] leading-[1.2] tracking-[0.02em] sm:text-h3"
          : "text-2xl sm:text-3xl font-bold tracking-tight font-mono"}>{title}</h1>
        {subtitle && <p className={g ? "text-muted-foreground mt-1" : "text-sm text-muted-foreground mt-1"}>{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// A screen's column: the handoff's main area stacks its blocks 24px apart
export function Page({ className, children, ...props }) {
  return <GraphiteScope className={cn("flex flex-col gap-6", className)} {...props}>{children}</GraphiteScope>;
}

// A section's title in the display face, with a muted note on the right
export function SectionTitle({ children, note, className, as: Tag = "h2" }) {
  return (
    <div className={cn("flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1", className)}>
      <Tag className="m-0 font-display text-[15px] leading-[1.35] tracking-[0.02em] font-semibold">{children}</Tag>
      {note && <span className="text-sm text-muted-foreground">{note}</span>}
    </div>
  );
}

// The uppercase label style (also table headers)
export function Label11({ className, children, ...props }) {
  return <span className={cn("text-xs uppercase tracking-[0.09em] text-muted-foreground", className)} {...props}>{children}</span>;
}

export function Card({ className, children, ...props }) {
  // Graphite: a flat surface with 10px corners, no border; hierarchy comes from the tone
  const base = useGraphite() ? "rounded-[10px] bg-card p-4" : "rounded-md border bg-card/60 p-5";
  return (
    <div className={cn(base, className)} {...props}>
      {children}
    </div>
  );
}

// Graphite: an uppercase label over a 40px numeral in Inter regular, on the card's own padding
export function StatCard({ label, value, sub, accent, testid }) {
  if (useGraphite()) return (
    <Card data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.09em] text-muted-foreground">{label}</div>
      <div className={cn("text-[24px] leading-none font-normal tabular-nums mt-3", accent)}>{value}</div>
      {sub && <div className="text-sm text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("text-2xl font-bold font-mono mt-2 tabular-nums", accent)}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

const STATUS = {
  active: "bg-green-500/15 text-green-500 border-green-500/30",
  online: "bg-green-500/15 text-green-500 border-green-500/30",
  locked: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  rebuilding: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  pending: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  offline: "bg-red-500/15 text-red-500 border-red-500/30",
  ended: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  retired: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

// Graphite: a status is a coloured word with its dot on a surface-2 tag; info/success is blue on purpose
const TONES = { info: "text-gr-success", warning: "text-gr-warning", danger: "text-gr-danger",
                muted: "text-muted-foreground", plain: "text-foreground" };
const GR_STATUS = {
  active: "info", online: "info", ready: "info", granted: "info", available: "info", "on sale": "info",
  locked: "warning", rebuilding: "warning", waiting: "warning", "in review": "warning", pending: "warning",
  processing: "warning", ending: "warning", "not checked": "warning", mismatch: "danger",
  offline: "danger", failed: "danger", revoked: "danger", suspended: "danger",
};

// `label`: the word to show when it isn't the status itself; `tone`: info, warning, danger, muted or plain; `live`: a
// session's badge, whose word shimmers while the session is active (running)
export function StatusBadge({ status, label, tone, className, live }) {
  const word = live && (status || "").toLowerCase() === "active" ? <TextShimmer>{label || status}</TextShimmer> : label || status;
  if (useGraphite()) return (
    <span className={cn("inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-gr-surface-2 px-2 py-0.5 text-xs font-medium uppercase tracking-[0.09em]",
      TONES[tone || GR_STATUS[(status || "").toLowerCase()] || "muted"], className)}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />{word}
    </span>
  );
  return (
    <Badge variant="outline" className={cn("font-mono text-[10px] uppercase tracking-wider", STATUS[status] || "")}>
      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 bg-current" />
      {word}
    </Badge>
  );
}

// A tag (NEW, SUSPENDED, RESIZE → cpx31): a 1px border in its tone
export function Tag({ tone = "plain", className, children, ...props }) {
  return (
    <span className={cn("inline-flex items-center whitespace-nowrap rounded px-1.5 border border-current text-xs uppercase tracking-[0.09em]",
      TONES[tone], className)} {...props}>{children}</span>
  );
}

// A type chip in a feed (USAGE, COMMIT): a raised fill, 4px corners
export function Chip({ className, children }) {
  return <span className={cn("rounded bg-gr-surface-2 px-1.5 py-0.5 text-xs uppercase tracking-[0.09em]", className)}>{children}</span>;
}

// A progress bar: 4px on the raised track, the primary fill; full turns danger. `marks` draws the alert points.
export function Bar({ pct, full, marks, className, thick }) {
  const w = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className={cn("relative rounded-sm bg-gr-surface-2", thick ? "h-1.5" : "h-1", className)}>
      <div className={cn("h-full rounded-sm", full ? "bg-gr-danger" : "bg-foreground")} style={{ width: `${w}%` }} />
      {marks && [50, 80].map((m) => (
        <span key={m} className="absolute -top-0.5 -bottom-0.5 w-px bg-muted-foreground" style={{ left: `${m}%` }} aria-hidden="true" />
      ))}
    </div>
  );
}

// A stat strip: cells on the surface with 1px hairline gaps between them; each cell is a label over its value
// `big`: a 24px value; `huge`: the 32px one the objective page uses
export function StatStrip({ items, className, cellClassName, min = 120 }) {
  return (
    <div className={cn("flex flex-wrap gap-px bg-gr-hairline rounded-[10px] overflow-hidden", className)}>
      {items.filter(Boolean).map((it) => (
        <div key={it.label} className={cn("bg-card px-4 py-3 flex flex-col gap-1 min-w-0", cellClassName)} style={{ flex: `1 1 ${it.min || min}px` }}
          data-testid={it.testid}>
          <Label11>{it.label}</Label11>
          <span className={cn("tabular-nums", it.huge ? "text-[32px] leading-none" : it.big ? "text-[24px] leading-7" : "font-semibold", it.className)}>{it.value}</span>
          {it.sub && <span className="text-sm text-muted-foreground">{it.sub}</span>}
        </div>
      ))}
    </div>
  );
}

// The footnote on every page that shows session metadata
export function PrivacyNote({ children, icon: Icon = EyeOff, className }) {
  return (
    <p className={cn("m-0 flex items-start gap-2 px-1 text-sm text-muted-foreground", className)}>
      <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" /><span>{children}</span>
    </p>
  );
}

// A person's initials on a raised circle
export function Avatar({ name, size = 24, className }) {
  const initials = (name || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  return (
    <span className={cn("grid place-items-center shrink-0 rounded-full bg-gr-surface-2 font-semibold", className)}
      style={{ width: size, height: size, fontSize: size >= 28 ? 11 : 10 }} aria-hidden="true">{initials}</span>
  );
}

// A segmented control: a 1px control border, the selected segment inverted
export function Segmented({ options, value, onChange, className, label, testid }) {
  return (
    <div role="radiogroup" aria-label={label} className={cn("inline-flex flex-wrap rounded-md border border-gr-control-border overflow-hidden", className)}
      data-testid={testid}>
      {options.map((o) => {
        const [v, text] = Array.isArray(o) ? o : [o, o];
        const on = v === value;
        return (
          <button key={v} type="button" role="radio" aria-checked={on} onClick={() => onChange(v)}
            data-testid={testid && `${testid}-${v}`}
            className={cn("h-[30px] px-3 font-semibold whitespace-nowrap transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
              on ? "bg-gr-selected text-gr-on-selected" : "hover:bg-gr-hover")}>{text}</button>
        );
      })}
    </div>
  );
}

// +added −removed, in the info and danger colours
export function Lines({ added, removed, className }) {
  return (
    <span className={cn("tabular-nums whitespace-nowrap", className)}>
      <span className="text-gr-success">+{int(added)}</span> <span className="text-gr-danger">−{int(removed)}</span>
    </span>
  );
}

// Graphite: an engine is its provider's logo and its label (components/ProviderLogo.jsx)
export function EngineBadge({ engine }) {
  const g = useGraphite();
  const map = {
    mock: { label: ENGINE_LABELS.mock, cls: "text-blue-500 border-blue-500/30 bg-blue-500/10" },
    claude: { label: ENGINE_LABELS.claude, cls: "text-amber-600 border-amber-600/30 bg-amber-600/10" },
    codex: { label: ENGINE_LABELS.codex, cls: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10" },
    muse: { label: ENGINE_LABELS.muse, cls: "text-sky-500 border-sky-500/30 bg-sky-500/10" },
  };
  const e = map[engine] || map.mock;
  if (g) return <EngineWithLogo engine={engine} className="text-sm" />;
  return <Badge variant="outline" className={cn("font-mono text-[10px]", e.cls)}>{e.label}</Badge>;
}

export const money = (n) => `$${Number(n || 0).toFixed(4)}`;
// dollars to the cent, with thousands separators ($1,204.50); `digits` 0 for whole dollars ($50)
export const usd = (n, digits = 2) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
export const int = (n) => Number(n || 0).toLocaleString("en-US");
// "1 session" / "2 sessions"
export const plural = (n, one, many = `${one}s`) => `${int(n)} ${n === 1 ? one : many}`;
// a runner's machine, or one session's share of it (backend/runner_routes.session_resources): "1.5 CPU · 3 GB"
export const resources = (r) => (r ? `${Number(r.cpus)} CPU · ${Number((r.memory_mb / 1024).toFixed(1))} GB` : "1 CPU · 1 GB");
export const pct = (spent, cap) => (cap ? Math.round((spent / cap) * 100) : 0);
export const shortId = (id) => (id || "").slice(0, 6);

// "22 Oct" (and "22 Oct, 13:55" with the time), as the handoff writes dates
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function shortDate(iso, time = false) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return time ? `${date}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : date;
}

// "just now", "12 min ago", "3 h ago", "Yesterday", then the date
export function ago(iso, now = Date.now()) {
  if (!iso) return "—";
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (s < 45) return s < 10 ? "just now" : `${Math.round(s)} s ago`;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86400) return "Yesterday";
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} days ago`;
  return shortDate(iso);
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> {label}…
    </div>
  );
}

export function Empty({ children, testid }) {
  const base = useGraphite() ? "text-muted-foreground rounded-[10px] bg-card" : "text-sm text-muted-foreground border border-dashed rounded-md";
  return (
    <div className={cn(base, "py-10 text-center")} data-testid={testid}>
      {children}
    </div>
  );
}
